import { createSlice } from '@reduxjs/toolkit';
// utils
import axios from 'src/utils/axios';
import parseQueryParams from '../../../utils/query';

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  error: false,
  unit: {},
  unitList: [],
  unitDropdownList: [],
  unitTypes: [],
  AMIs: {},
  preferenceGroupsList: {},
  programTypesList: [],
  unitOverview: {},
  households: [],
  isLoadingUnitOverview: true,
  certifications: [],
};

// AMIs and preferenceGroupsList are keyed by property id so a property's
// catalogue can never leak into another property's pickers (ENG-3714).
// The catalogue list endpoints are property-scoped and require `?property=<id>`.

const slice = createSlice({
  name: 'Unit',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    // GET Single property
    getUnitSuccess(state, action) {
      state.isLoading = false;
      state.unit = action.payload;
    },

    // GET Property list
    getUnitListSuccess(state, action) {
      state.isLoading = false;
      state.unitList = action.payload;
    },

    createUnitSuccess(state) {
      state.isLoading = false;
    },

    updateUnitSuccess(state, action) {
      state.isLoading = false;
      state.unit = action.payload;
    },

    getUnitTypesSuccess(state, action) {
      state.isLoading = false;
      state.unitTypes = action.payload;
    },

    getAMIsSuccess(state, action) {
      state.isLoading = false;
      const { propertyId, amis } = action.payload;
      if (propertyId != null) {
        state.AMIs[propertyId] = amis;
      }
    },

    getPreferenceGroupsSuccess(state, action) {
      state.isLoading = false;
      const { propertyId, preferenceGroups } = action.payload;
      if (propertyId != null) {
        state.preferenceGroupsList[propertyId] = preferenceGroups;
      }
    },

    getProgramTypesSuccess(state, action) {
      state.isLoading = false;
      state.programTypesList = action.payload;
    },

    getUnitDropdownListSuccess(state, action) {
      state.isLoading = false;
      state.unitDropdownList = action.payload;
    },

    getUnitOverviewSuccess(state, action) {
      state.isLoading = false;
      state.unitOverview = action.payload;
      state.unit = { ...state.unit, ...action.payload };
      state.households = action.payload?.households || [];
      state.certifications = action.payload?.certifications || [];
      state.isLoadingUnitOverview = false;
    },

    getUnitOverviewFailure(state) {
      state.isLoading = false;
      state.isLoadingUnitOverview = false;
      state.error = 'No units were found.';
    },

    setHouseholds(state, action) {
      state.households = action.payload;
    },
    setisLoadingUnitOverview(state, action) {
      state.isLoadingUnitOverview = action.payload;
    },
  },
});

// Reducer
export default slice.reducer;

export const { setHouseholds } = slice.actions;

// ----------------------------------------------------------------------

export function getUnit(unitId, queryParams) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(
        `property_portfolio/units/${unitId}/?${parseQueryParams(queryParams)}`
      );
      dispatch(slice.actions.getUnitSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getUnitDropdownList(propertyId, applicantPoolID, queryParams) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      let url = `property_portfolio/units/?building__property=${propertyId}`;
      if (applicantPoolID != null) {
        url += `&building__applicant_pool=${applicantPoolID}`;
      }
      url += `&${parseQueryParams(queryParams)}`;
      const response = await axios.get(url);
      dispatch(slice.actions.getUnitDropdownListSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function createUnit(unitData) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post('property_portfolio/units/', unitData);
      dispatch(slice.actions.createUnitSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function updateUnit(unitId, unitData, queryParams) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.patch(
        `property_portfolio/units/${unitId}/?${parseQueryParams(queryParams)}`,
        unitData
      );
      dispatch(slice.actions.updateUnitSuccess(response.data));
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error; // Rethrow so callers can handle the error
    }
  };
}

export function getUnitTypes() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get('property_portfolio/unit_type/');
      dispatch(slice.actions.getUnitTypesSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getPreferenceGroupsList(propertyId) {
  return async (dispatch) => {
    // ENG-3809: the unit surfaces dispatch this on mount, before the selected
    // property has resolved. Without an id the request would go out as
    // `property=undefined` and come back as an error; skip it — the effect
    // re-runs as soon as the id is available.
    if (!propertyId) {
      return;
    }
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(
        `property_portfolio/unit_preference_group/?property=${propertyId}`
      );

      // Deduplicate preference groups by ID to prevent duplicates
      const uniquePreferenceGroups = response.data.reduce((acc, current) => {
        const x = acc.find((item) => item.id === current.id);
        if (!x) {
          return acc.concat([current]);
        } else {
          return acc;
        }
      }, []);

      dispatch(
        slice.actions.getPreferenceGroupsSuccess({
          propertyId,
          preferenceGroups: uniquePreferenceGroups.map((x) => ({
            id: x.id,
            name: x.name,
            label: x.name,
          })),
        })
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getProgramTypesList() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get('certification/program_type/');
      dispatch(slice.actions.getProgramTypesSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getAMIsList(propertyId) {
  return async (dispatch) => {
    // ENG-3809: see getPreferenceGroupsList — never request without a property.
    if (!propertyId) {
      return;
    }
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(
        `property_portfolio/ami/?property=${propertyId}&kind=unit`
      );
      dispatch(
        slice.actions.getAMIsSuccess({ propertyId, amis: response.data })
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getUnitOverview(unitId) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.setisLoadingUnitOverview(true));
      const response = await axios.get(`unit_overview/?unit=${unitId}`);
      const unitData = response.data?.length > 0 ? response.data[0] : null;
      if (!unitData) {
        dispatch(slice.actions.getUnitOverviewFailure());
        return unitData;
      }
      dispatch(slice.actions.getUnitOverviewSuccess(unitData));
      return unitData;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error; // Rethrow so callers can handle the error
    }
  };
}
